import { useEffect, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { GeneralErrorIndicator } from '@/components/GeneralErrorIndicator';
import { NavigationBar } from '@/components/NavigationBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';

import { ContactSupportLink } from './ContactSupportLink';

const formId = 'order-lookup-verify-form';

const formSchema = z.object({
  code: z
    .string()
    .length(6, 'Code must be 6 digits')
    .regex(/^\d{6}$/, 'Code must contain only numbers'),
});

type OrderLookupVerifyFormData = z.infer<typeof formSchema>;

interface OrderLookupVerifyStepProps {
  onBack: () => void;
  onSubmit: (code: string) => void;
  onResend: () => void;
  isVerifying: boolean;
  isResending: boolean;
  canResend: boolean;
  remainingMs: number;
  resendError: boolean;
  error?: string;
}

export function OrderLookupVerifyStep({
  onBack,
  onSubmit,
  onResend,
  isVerifying,
  isResending,
  canResend,
  remainingMs,
  resendError,
  error,
}: OrderLookupVerifyStepProps) {
  const otpInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<OrderLookupVerifyFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { code: '' },
  });

  const submit = form.handleSubmit((values) => onSubmit(values.code));

  useEffect(
    function clearCodeOnError() {
      if (error) {
        form.reset({ code: '' });
        otpInputRef.current?.focus();
      }
    },
    // oxlint-disable-next-line exhaustive-deps
    [error]
  );

  useEffect(
    function focusWhenNotVerifying() {
      if (!isVerifying) {
        otpInputRef.current?.focus();
      }
    },
    [isVerifying]
  );

  return (
    <section>
      <NavigationBar onBack={onBack} backLabel="Back" />
      <Card>
        <CardHeader>
          <CardTitle>Check your text</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8 sm:space-y-12">
          <p className="text-base text-main">
            If an account is associated with this phone number, you’ll receive a 6-digit code.
          </p>

          <form id={formId} onSubmit={submit} className="space-y-8 sm:space-y-12">
            <Controller
              name="code"
              control={form.control}
              render={({ field, fieldState }) => {
                const inputId = `${formId}-code`;
                const fieldError = error ? { message: error, type: 'manual' } : fieldState.error;

                return (
                  <Field orientation="vertical" data-invalid={Boolean(fieldError)}>
                    <FieldLabel htmlFor={inputId}>Enter the 6-digit code</FieldLabel>
                    <InputOTP
                      // oxlint-disable-next-line no-autofocus
                      autoFocus={true}
                      disabled={isVerifying}
                      minLength={6}
                      maxLength={6}
                      id={inputId}
                      ref={otpInputRef}
                      value={field.value}
                      onComplete={submit}
                      pattern={REGEXP_ONLY_DIGITS}
                      inputMode="numeric"
                      onChange={field.onChange}
                      aria-invalid={Boolean(fieldError)}
                    >
                      <InputOTPGroup className="min-w-0 flex-1 gap-2 *:data-[slot=input-otp-slot]:h-[60px] *:data-[slot=input-otp-slot]:max-w-12 *:data-[slot=input-otp-slot]:min-w-0 *:data-[slot=input-otp-slot]:flex-1 *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border">
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>

                    {fieldError?.message && <FieldError errors={[fieldError]} />}
                  </Field>
                );
              }}
            />

            {resendError && (
              <GeneralErrorIndicator>
                Failed to resend verification code. Please try again.
              </GeneralErrorIndicator>
            )}

            <div className="space-y-4.5">
              <div className="flex justify-center sm:justify-start">
                <button
                  type="button"
                  onClick={onResend}
                  disabled={isResending || !canResend}
                  className="hover:text-hydrangea-700 text-sm font-medium text-hydrangea-500 underline underline-offset-auto transition-colors disabled:opacity-50"
                >
                  {getResendLabel({ isResending, canResend, remainingMs })}
                </button>
              </div>

              <Button
                type="submit"
                disabled={isVerifying}
                className="w-full sm:w-auto sm:min-w-[186px]"
              >
                {isVerifying ? 'Verifying...' : 'Confirm'}
              </Button>
            </div>
          </form>

          <ContactSupportLink />
        </CardContent>
      </Card>
    </section>
  );
}

function getResendLabel({
  isResending,
  canResend,
  remainingMs,
}: {
  isResending: boolean;
  canResend: boolean;
  remainingMs: number;
}) {
  if (isResending) return 'Sending code...';
  if (canResend) return 'Don’t see it? Resend code';
  return `Don’t see it? Resend code in ${Math.ceil(remainingMs / 1000)}s`;
}
